import { generateKey } from '#utils';

export const LOCAL_STORAGE_KEYS = {
  PARTIAL_LOGIN_STATE: 'PartialLoginState',
  USER_SIGNEDIN: 'USER_SIGNEDIN',
  OTTER_SIGNUP_TS: 'OTTER_SIGNUP_TS',
  OTTER_LRU_PUBLIC_SPEECH: 'OTTER_LRU_PUBLIC_SPEECH',
  POST_AUTH_NOTIFICATION_MSG: 'POST_AUTH_NOTIFICATION_MSG',
  HAS_CLOSED_OR_CONNECTED_ZOOM_COMPLIANCE_BANNER:
    'HAS_CLOSED_OR_CONNECTED_ZOOM_COMPLIANCE_BANNER',
  SKIP_SILENCE: 'SKIP_SILENCE',
  PLAY_SPEED: 'PLAY_SPEED',
  ACCOUNT_SETTINGS_TAB_ELIGIBILITY: 'ACCOUNT_SETTINGS_TABS_ELIGIBILITY',
  CAMPAIGN_PAID_MEDIA_SIGNUP_FLOW: 'CAMPAIGN.PAID_MEDIA_SIGNUP_FLOW',
};

// Simple base64 encoding/decoding functions
const encode = (value: string | null): string => {
  if (value === null) return '';
  try {
    return btoa(value);
  } catch (e) {
    console.warn('Failed to encode value:', e);
    return value;
  }
};

const decode = (value: string | null): string | null => {
  if (!value) return null;
  try {
    return atob(value);
  } catch (e) {
    console.warn('Failed to decode value:', e);
    return value;
  }
};

const browserCache = new Map<string, unknown>();

// Check if running in iframe
const isInIframe = (): boolean => {
  try {
    return window.location !== window.parent.location;
  } catch (_e) {
    return true; // Assume iframe if we can't access parent
  }
};

// Browser cache utilities
const setInBrowserCache = (key: string, value: unknown): void => {
  browserCache.set(key, value);
};

const deleteFromBrowserCache = (key: string): void => {
  browserCache.delete(key);
};

/**
 *
 * @deprecated Avoid introducing new localStorage usage.
 *
 * WARNING: localStorage is unreliable on this origin.
 * For some clients, localStorage is already near/over quota, and
 * `localStorage.setItem(...)` regularly throws `QuotaExceededError`.
 *
 * Treat localStorage as best-effort cache only.
 * Do NOT use it for critical flows (auth, onboarding, routing, required user state).
 * Prefer server-backed persistence or other resilient storage.
 * Store a value in localStorage with encoding
 * @param key - The storage key
 * @param value - The value to store (can be any type)
 * @param ignorePrefix - Whether to ignore the key prefix
 */
export const setInLocalStorage = (
  key: string,
  value: unknown,
  ignorePrefix = false,
  skipEncode = false,
): void => {
  // Only when not embedded in an iframe
  if (!isInIframe()) {
    const storageKey = generateKey(key, ignorePrefix);

    // Always store a copy in memory
    setInBrowserCache(storageKey, value);

    try {
      if (typeof value === 'object') {
        const storageValue = value === null ? '' : JSON.stringify(value);
        localStorage.setItem(
          storageKey,
          skipEncode ? storageValue : encode(storageValue),
        );
      } else {
        const storageValue = value === undefined ? 'null' : String(value);
        localStorage.setItem(
          storageKey,
          skipEncode ? storageValue : encode(storageValue),
        );
      }
    } catch (_e) {
      console.error('Failed to set localStorage item:', _e);
    }
  }
};

/**
 * Retrieve a value from localStorage with decoding
 * @param key - The storage key
 * @param ignorePrefix - Whether to ignore the key prefix
 * @param skipDecode - Whether to skip decoding the value
 * @returns The decoded value or null
 */
export const getFromLocalStorage = <T = unknown>(
  key: string,
  ignorePrefix = false,
  skipDecode = false,
): T | null => {
  // Only when in browser and not embedded in an iframe
  if (typeof window !== 'undefined' && !isInIframe()) {
    try {
      const storageKey = generateKey(key, ignorePrefix);
      const value = localStorage.getItem(storageKey);

      let decodedValue = value;
      if (skipDecode === false) {
        decodedValue = decode(value);
      }

      if (decodedValue === null) {
        return null;
      }

      try {
        const parsedValue = JSON.parse(decodedValue);
        return parsedValue as T;
      } catch (_e) {
        return decodedValue as unknown as T;
      }
    } catch (_e) {
      console.error('Failed to get localStorage item:', _e);
      return null;
    }
  }
  return null;
};

/**
 * Remove a value from localStorage and browser cache
 * @param key - The storage key
 * @param ignorePrefix - Whether to ignore the key prefix
 */
export const removeFromLocalStorage = (
  key: string,
  ignorePrefix = false,
): void => {
  try {
    const storageKey = generateKey(key, ignorePrefix);
    localStorage.removeItem(storageKey);
    deleteFromBrowserCache(storageKey);
  } catch (_e) {
    console.error('Failed to remove localStorage item:', _e);
  }
};

/**
 * Clear all localStorage items with the app prefix
 */
export const clearAppLocalStorage = (): void => {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
      deleteFromBrowserCache(key);
    }
  } catch (_e) {
    console.error('Failed to clear app localStorage:', _e);
  }
};

/**
 * Get all localStorage keys with the app prefix
 */
export const getAppLocalStorageKeys = (): string[] => {
  try {
    const keys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }

    return keys;
  } catch (_e) {
    console.error('Failed to get localStorage keys:', _e);
    return [];
  }
};
